import { OAUTH_STRATEGIES } from '@/features/auth/login/consts/oauth';
import type { OAuthStatusCheckResult, Strategy } from '@/features/auth/login/types/login';
import {
  CLERK_ERROR_CODE,
  CLERK_OAUTH_STATUS,
  CLERK_RESOURCE_STATUS,
  CLERK_VERIFICATION_STATUS,
  OAUTH_STATUS
} from '@/features/auth/shared/consts/clerk';
import type { useSignIn, useSignUp } from '@clerk/clerk-react';

type SignInResource = ReturnType<typeof useSignIn>['signIn'];
type SignUpResource = ReturnType<typeof useSignUp>['signUp'];

export const parseClerkOAuthStatus = (
  signIn: SignInResource,
  signUp: SignUpResource,
  searchParams: URLSearchParams
): OAuthStatusCheckResult => {
  if (!signIn || !signUp) {
    return { status: OAUTH_STATUS.IDLE };
  }

  const clerkStatus = searchParams.get('__clerk_status');
  if (clerkStatus && clerkStatus !== CLERK_OAUTH_STATUS.VERIFIED) {
    return { status: OAUTH_STATUS.PROCESSING };
  }

  if (signIn.status === CLERK_RESOURCE_STATUS.COMPLETE && signIn.createdSessionId) {
    return {
      status: OAUTH_STATUS.SIGN_IN_COMPLETE,
      sessionId: signIn.createdSessionId
    };
  }

  if (signUp.status === CLERK_RESOURCE_STATUS.COMPLETE && signUp.createdSessionId) {
    return {
      status: OAUTH_STATUS.SIGN_UP_COMPLETE,
      sessionId: signUp.createdSessionId
    };
  }

  if (signIn.status !== CLERK_RESOURCE_STATUS.NEEDS_IDENTIFIER) {
    return { status: OAUTH_STATUS.IDLE };
  }

  const verification = signIn.firstFactorVerification;
  const isTransferable = verification.status === CLERK_VERIFICATION_STATUS.TRANSFERABLE;
  const hasError = !!verification.error;
  const isOAuthStrategy =
    verification.strategy !== null && (Object.values(OAUTH_STRATEGIES) as string[]).includes(verification.strategy);

  if (!isTransferable || !hasError || !isOAuthStrategy) {
    return { status: OAUTH_STATUS.IDLE };
  }

  const errorCode = verification.error?.code;
  const errorMessage = verification.error?.longMessage ?? verification.error?.message;

  if (errorCode === CLERK_ERROR_CODE.EXTERNAL_ACCOUNT_NOT_FOUND) {
    return {
      status: OAUTH_STATUS.NEEDS_ACCOUNT_CREATION,
      strategy: verification.strategy as Strategy,
      errorCode,
      errorMessage
    };
  }

  if (errorCode === CLERK_ERROR_CODE.IDENTIFIER_ALREADY_SIGNED_IN) {
    return { status: OAUTH_STATUS.ALREADY_SIGNED_IN };
  }

  return {
    status: OAUTH_STATUS.OAUTH_ERROR,
    errorCode,
    errorMessage
  };
};
